import { DocsCallout, DocsContent, DocsDiagram, DocsLayout, H2, H3 } from '@/components/docs'
import { Card } from '@/components/ui/card'

export default function DocsPipelinesTabular() {
  return (
    <DocsLayout>
      <DocsContent
        title="Tabular Extraction Pipeline"
        description="How XENDREL processes CSV and Excel files through LLM-driven column mapping and row-by-row entity extraction into the knowledge graph."
        previousPage={{ title: 'Text Extraction', href: '/docs/pipelines/text' }}
        nextPage={{ title: 'Geospatial Pipeline', href: '/docs/pipelines/geospatial' }}
      >
        <H2 id="overview">Overview</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          The Tabular Extraction pipeline handles structured data in CSV and Excel files. Unlike
          text extraction which reads unstructured prose, this pipeline maps specific columns to
          entity properties and processes data row by row for precise, repeatable extraction.
        </p>

        <DocsDiagram>{`CSV/XLSX File
    ↓
Phase 1: Parse → Parquet on S3 → column analysis
    ↓
Phase 2: Tabular Extraction
    ├─ LLM analyzes columns + sample rows
    ├─ Proposes entity/relationship mappings
    ├─ User reviews in Wizard Configuration UI
    ├─ TabularKGExtractor processes row-by-row:
    │   ├─ Evaluate row conditions (filters)
    │   ├─ MERGE entity nodes by _key
    │   ├─ Map column values → node properties
    │   └─ Create relationships between entities
    └─ Output: entities + relationships → Neo4j`}</DocsDiagram>

        <H2 id="free-text">Extract from free text</H2>
        <p className="mb-4 leading-relaxed text-foreground">
          In the mapping review, tick message bodies, notes or descriptions mapped to entity
          properties under Extract from free text. The assistant proposes the selection; you can
          change it. The review counts non-empty cells and the button says Add to the case and
          extract or Update the case and extract. Text on relationships is not offered.
        </p>
        <p className="mb-4 leading-relaxed text-foreground">
          The table is projected first, then a separate run extracts with the case schema. Entities
          are linked to every cell holder that names them; relationships stay within one cell.
          Unattached entities and failed rows are counted in the run. Unchanged and identical text
          reuses stored results, failed chunks retry on the next update, and unticking removes the
          extraction contribution. Source text and search snippets stay unchanged.
        </p>

        <H2 id="column-mapping">Column Mapping</H2>

        <H3 id="auto-analysis">Automatic Analysis</H3>

        <p className="text-foreground mb-4 leading-relaxed">
          When a CSV or Excel file enters Phase 2, the{' '}
          <strong className="text-foreground">Tabular Analyzer</strong> automatically examines the
          column headers and a sample of rows to propose entity and relationship mappings:
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Entity proposals</strong> — Which columns map to
              which entity types (e.g., "sender_name" → Person, "account_number" → BankAccount)
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Relationship proposals</strong> — How entities in
              different columns relate (e.g., sender → TRANSACTION_TO → receiver)
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Confidence scores</strong> — How certain the
              analyzer is about each mapping
            </span>
          </li>
        </ul>

        <H3 id="wizard-configuration">Wizard Configuration</H3>

        <p className="text-foreground mb-4 leading-relaxed">
          The auto-proposed mappings are presented in the Wizard Configuration UI where you can:
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Accept, modify, or reject proposed entity mappings</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Set identifier columns (which column uniquely identifies each entity)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Configure row conditions (filters to include/exclude specific rows)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Add static properties (values applied to every extracted entity)</span>
          </li>
        </ul>

        <DocsCallout variant="info" title="Manual Schema">
          The Wizard Configuration is saved as a{' '}
          <code className="text-amber-600 dark:text-amber-400">WizardConfiguration</code> JSON in
          the DocumentExtractionScope. This means you can re-run extraction with the same mapping,
          or modify it and reprocess.
        </DocsCallout>

        <H2 id="extraction-process">Extraction Process</H2>

        <H3 id="row-processing">Row-by-Row Processing</H3>

        <p className="text-foreground mb-4 leading-relaxed">
          The <strong className="text-foreground">TabularKGExtractor</strong> reads the Parquet file
          row by row and for each row:
        </p>

        <ol className="space-y-3 my-4 text-foreground list-decimal list-inside">
          <li>
            <strong className="text-foreground">Evaluates row conditions</strong> — Checks if the
            row matches any configured filters (e.g., only process rows where "type" =
            "transaction")
          </li>
          <li>
            <strong className="text-foreground">Generates entity key</strong> — Creates a stable{' '}
            <code className="text-amber-600 dark:text-amber-400 bg-muted px-1 rounded">_key</code>{' '}
            from the identifier column (or a hash of properties if no identifier is set)
          </li>
          <li>
            <strong className="text-foreground">MERGE nodes</strong> — Uses Neo4j MERGE on{' '}
            <code className="text-amber-600 dark:text-amber-400 bg-muted px-1 rounded">
              (label, _key)
            </code>{' '}
            pairs, making the operation idempotent — running it twice produces the same result
          </li>
          <li>
            <strong className="text-foreground">Maps properties</strong> — Copies column values to
            node properties according to the mapping configuration
          </li>
          <li>
            <strong className="text-foreground">Creates relationships</strong> — When both source
            and target entities exist, creates the configured relationship between them
          </li>
        </ol>

        <H3 id="entity-merge">Entity Merge Strategy</H3>

        <p className="text-foreground mb-4 leading-relaxed">
          The <code className="text-amber-600 dark:text-amber-400 bg-muted px-1 rounded">_key</code>{' '}
          property is critical for deduplication. When multiple rows reference the same entity
          (e.g., the same person appears in 50 transactions), MERGE ensures only one node is created
          and properties are accumulated.
        </p>

        <DocsDiagram>{`Example: Bank statement CSV
Row 1: sender=John Doe, receiver=ACME Corp, amount=500
Row 2: sender=John Doe, receiver=Beta LLC,  amount=300

Result in Neo4j:
  (Person {_key: "john-doe", name: "John Doe"})
  (Company {_key: "acme-corp", name: "ACME Corp"})
  (Company {_key: "beta-llc", name: "Beta LLC"})
  (John Doe)-[TRANSACTION_TO {amount: 500}]->(ACME Corp)
  (John Doe)-[TRANSACTION_TO {amount: 300}]->(Beta LLC)`}</DocsDiagram>

        <H2 id="supported-patterns">Supported Data Patterns</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          The Tabular pipeline auto-detects and optimizes for several data patterns:
        </p>

        <div className="space-y-4 my-6">
          <PatternCard
            pattern="Generic CSV"
            description="Standard tabular data with entities and relationships. Contact lists, registry exports, transaction records."
            example="name, company, email, phone, address"
          />
          <PatternCard
            pattern="Blockchain Transactions"
            description="Crypto transaction data with wallet addresses, amounts, and timestamps. Auto-detects blockchain-specific columns."
            example="tx_hash, from_address, to_address, amount, token, block_number"
          />
          <PatternCard
            pattern="Bank Statements"
            description="Financial transaction records with accounts, amounts, dates, and counterparties."
            example="date, description, debit, credit, balance, reference"
          />
        </div>

        <DocsCallout variant="warning" title="When to Use Geospatial Instead">
          If your CSV contains latitude/longitude columns with timestamps (GPS tracks, CDR records,
          AIS data), use the <strong>Geospatial Pipeline</strong> instead. The Tabular pipeline
          would create one Neo4j node per row — which causes severe performance issues for
          time-series data with thousands of observations. The Geospatial pipeline stores
          observations efficiently in Parquet and only extracts unique entities to Neo4j.
        </DocsCallout>

        <H2 id="output">Output</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          After extraction completes, you can review:
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Nodes created/merged</strong> — Count of new vs
              existing entities
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Relationships created</strong> — Connections
              between entities
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Rows processed</strong> — Total rows read from the
              Parquet file
            </span>
          </li>
        </ul>

        <p className="text-foreground mb-4 leading-relaxed">
          All entities are visible in the Graph Explorer and can be queried with Natural Language
          queries.
        </p>
      </DocsContent>
    </DocsLayout>
  )
}

function PatternCard({
  pattern,
  description,
  example,
}: {
  pattern: string
  description: string
  example: string
}) {
  return (
    <Card size="compact" className="rounded-lg p-4 bg-card/50">
      <div className="font-semibold text-foreground mb-2">{pattern}</div>
      <p className="text-sm text-muted-foreground mb-2">{description}</p>
      <div className="text-xs font-mono text-muted-foreground">
        <span className="text-amber-500/70">Columns:</span> {example}
      </div>
    </Card>
  )
}
